'use client';

import { useRef, useEffect, useState } from 'react';
import Image from 'next/image';
import { useLocale } from '@/lib/i18n/LocaleContext';

// Loose tween type — kept local so this module doesn't pull gsap types into
// the homepage entry chunk graph (TASK-4).
type TweenLike = { timeScale: (n: number) => unknown };

export function StatementSection() {
  const { dict } = useLocale();
  const sectionRef = useRef<HTMLDivElement>(null);
  const row1WrapRef = useRef<HTMLDivElement>(null);
  const row1InnerRef = useRef<HTMLDivElement>(null);
  const row2WrapRef = useRef<HTMLDivElement>(null);
  const row2InnerRef = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState(false);

  // Refs for the GSAP tween objects so we can speed them up/slow them on hover
  const tween1Ref = useRef<TweenLike | null>(null);
  const tween2Ref = useRef<TweenLike | null>(null);

  // GSAP loaded dynamically so it doesn't ship in the homepage entry chunk
  // (TASK-4).
  useEffect(() => {
    if (!sectionRef.current) return;

    let ctx: { revert: () => void } | undefined;
    let cancelled = false;

    (async () => {
      const { gsap } = await import('@/lib/gsap');
      if (cancelled) return;

      ctx = gsap.context(() => {
      if (!row1InnerRef.current || !row2InnerRef.current) return;

      const w1 = row1InnerRef.current.offsetWidth / 2;
      const w2 = row2InnerRef.current.offsetWidth / 2;

      // Row 1: slides left
      tween1Ref.current = gsap.fromTo(
        row1InnerRef.current,
        { x: 0 },
        { x: -w1, duration: 28, ease: 'none', repeat: -1, paused: true }
      );

      // Row 2: slides right
      tween2Ref.current = gsap.fromTo(
        row2InnerRef.current,
        { x: -w2 },
        { x: 0, duration: 32, ease: 'none', repeat: -1, paused: true }
      );

      // The marquee is infinite — never let it tick while offscreen. GSAP's
      // ticker otherwise burns main-thread for the whole session (visible as
      // seconds of "script" time in mobile traces, task-28).
      const io = new IntersectionObserver((entries) => {
        const visible = entries.some((e) => e.isIntersecting);
        const t1 = tween1Ref.current as { paused: (v?: boolean) => unknown } | null;
        const t2 = tween2Ref.current as { paused: (v?: boolean) => unknown } | null;
        t1?.paused(!visible);
        t2?.paused(!visible);
      });
      if (row1WrapRef.current) io.observe(row1WrapRef.current);

      // Section fade in
      gsap.from(sectionRef.current, {
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 85%',
        },
        opacity: 0,
        y: 20,
        duration: 1.5,
        ease: 'power3.out',
      });

      return () => io.disconnect();
    }, sectionRef);
    })();

    return () => {
      cancelled = true;
      ctx?.revert();
    };
  }, []);

  // Hover effect: slow down & sharpen on hover, resume on leave
  const handleMarqueeEnter = () => {
    setHovered(true);
    tween1Ref.current?.timeScale(0.3);
    tween2Ref.current?.timeScale(0.3);
  };

  const handleMarqueeLeave = () => {
    setHovered(false);
    tween1Ref.current?.timeScale(1);
    tween2Ref.current?.timeScale(1);
  };

  // Row 1 items – filled white text + ✦ separators
  const row1Content = (
    <>
      <span className="rtl-display-m font-six-caps text-[70px] md:text-[110px] lg:text-[150px] leading-none text-white tracking-wider uppercase">{dict.statement.row1.webApps}</span>
      <span className="mx-6 md:mx-10 text-4xl md:text-5xl text-white/30 select-none transition-colors duration-300 group-hover/marquee:text-white/60">✦</span>
      <span className="rtl-display-m font-six-caps text-[70px] md:text-[110px] lg:text-[150px] leading-none tracking-wider uppercase transition-colors duration-300" style={{ WebkitTextStroke: '1px white', color: 'transparent' }}>{dict.statement.row1.aiTools}</span>
      <span className="mx-6 md:mx-10 text-4xl md:text-5xl text-white/30 select-none transition-colors duration-300 group-hover/marquee:text-white/60">✦</span>
      <span className="rtl-display-m font-six-caps text-[70px] md:text-[110px] lg:text-[150px] leading-none text-white tracking-wider uppercase">{dict.statement.row1.mobile}</span>
      <span className="mx-6 md:mx-10 text-4xl md:text-5xl text-white/30 select-none transition-colors duration-300 group-hover/marquee:text-white/60">✦</span>
      <span className="rtl-display-m font-six-caps text-[70px] md:text-[110px] lg:text-[150px] leading-none tracking-wider uppercase transition-colors duration-300" style={{ WebkitTextStroke: '1px white', color: 'transparent' }}>{dict.statement.row1.platforms}</span>
      <span className="mx-6 md:mx-10 text-4xl md:text-5xl text-white/30 select-none transition-colors duration-300 group-hover/marquee:text-white/60">✦</span>
      <span className="rtl-display-m font-six-caps text-[70px] md:text-[110px] lg:text-[150px] leading-none text-white tracking-wider uppercase">{dict.statement.row1.production}</span>
      <span className="mx-6 md:mx-10 text-4xl md:text-5xl text-white/30 select-none transition-colors duration-300 group-hover/marquee:text-white/60">✦</span>
    </>
  );

  // Row 2 items – ghost outline + numbered badge separators
  const row2Content = (
    <>
      <span className="rtl-display-m font-six-caps text-[70px] md:text-[110px] lg:text-[150px] leading-none tracking-wider uppercase transition-colors duration-300" style={{ WebkitTextStroke: '1px rgba(255,255,255,0.4)', color: 'transparent' }}>{dict.statement.row2.design}</span>
      <span className="mx-6 md:mx-10 inline-flex items-center justify-center w-10 h-10 md:w-14 md:h-14 rounded-full border border-white/30 text-white/40 text-sm font-mono transition-all duration-300 group-hover/marquee:border-white/60 group-hover/marquee:text-white/60">01</span>
      <span className="rtl-display-m font-six-caps text-[70px] md:text-[110px] lg:text-[150px] leading-none text-white tracking-wider uppercase">{dict.statement.row2.build}</span>
      <span className="mx-6 md:mx-10 inline-flex items-center justify-center w-10 h-10 md:w-14 md:h-14 rounded-full border border-white/30 text-white/40 text-sm font-mono transition-all duration-300 group-hover/marquee:border-white/60 group-hover/marquee:text-white/60">02</span>
      <span className="rtl-display-m font-six-caps text-[70px] md:text-[110px] lg:text-[150px] leading-none tracking-wider uppercase transition-colors duration-300" style={{ WebkitTextStroke: '1px rgba(255,255,255,0.4)', color: 'transparent' }}>{dict.statement.row2.ship}</span>
      <span className="mx-6 md:mx-10 inline-flex items-center justify-center w-10 h-10 md:w-14 md:h-14 rounded-full border border-white/30 text-white/40 text-sm font-mono transition-all duration-300 group-hover/marquee:border-white/60 group-hover/marquee:text-white/60">03</span>
      <span className="rtl-display-m font-six-caps text-[70px] md:text-[110px] lg:text-[150px] leading-none text-white tracking-wider uppercase">{dict.statement.row2.scale}</span>
      <span className="mx-6 md:mx-10 inline-flex items-center justify-center w-10 h-10 md:w-14 md:h-14 rounded-full border border-white/30 text-white/40 text-sm font-mono transition-all duration-300 group-hover/marquee:border-white/60 group-hover/marquee:text-white/60">04</span>
      <span className="rtl-display-m font-six-caps text-[70px] md:text-[110px] lg:text-[150px] leading-none tracking-wider uppercase transition-colors duration-300" style={{ WebkitTextStroke: '1px rgba(255,255,255,0.4)', color: 'transparent' }}>{dict.statement.row2.support}</span>
      <span className="mx-6 md:mx-10 inline-flex items-center justify-center w-10 h-10 md:w-14 md:h-14 rounded-full border border-white/30 text-white/40 text-sm font-mono transition-all duration-300 group-hover/marquee:border-white/60 group-hover/marquee:text-white/60">05</span>
    </>
  );

  return (
    <section
      ref={sectionRef}
      className="pt-24 md:pt-36 w-full relative z-20"
    >
      {/* Bottom soft border */}
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>

      {/* Marquee rows — hover slows both down */}
      <div
        className="group/marquee cursor-default"
        onMouseEnter={handleMarqueeEnter}
        onMouseLeave={handleMarqueeLeave}
      >
        {/* Hover overlay: subtle bright veil that appears on hover */}
        <div
          className="pointer-events-none absolute inset-x-0 transition-opacity duration-500"
          style={{
            top: 0,
            height: '340px',
            background: 'linear-gradient(to bottom, rgba(0,255,180,0.04) 0%, transparent 100%)',
            opacity: hovered ? 1 : 0,
          }}
        />

        {/* ─── Row 1: scrolls LEFT ─── */}
        <div
          ref={row1WrapRef}
          className="relative w-full h-[90px] md:h-[130px] lg:h-[165px] overflow-hidden mb-3 md:mb-4"
        >
          <div
            ref={row1InnerRef}
            className="absolute top-0 left-0 flex items-center whitespace-nowrap"
            style={{ willChange: 'transform' }}
          >
            {row1Content}{row1Content}
          </div>
        </div>

        {/* ─── Row 2: scrolls RIGHT ─── */}
        <div
          ref={row2WrapRef}
          className="relative w-full h-[90px] md:h-[130px] lg:h-[165px] overflow-hidden"
        >
          <div
            ref={row2InnerRef}
            className="absolute top-0 left-0 flex items-center whitespace-nowrap"
            style={{ willChange: 'transform' }}
          >
            {row2Content}{row2Content}
          </div>
        </div>
      </div>

      {/* Large Floating App Mockup (No Background) */}
      <div className="relative w-full flex justify-center perspective-[2000px] overflow-hidden md:overflow-visible mt-[-4rem] md:mt-[-3rem] lg:mt-[-4rem]">
        {/* Shifted top-left on mobile to show the hand completely and prevent right-overflow cutoffs */}
        <div className="w-[200%] sm:w-[160%] md:w-auto relative left-0 -translate-x-[10%] md:translate-x-0 z-10 origin-bottom flex justify-center">
          <Image
            src="/images/multi_2.webp"
            alt={dict.statement.mockupAlt}
            width={1600}
            height={1067}
            sizes="(max-width: 640px) 200vw, (max-width: 768px) 160vw, 720px"
            className="w-full md:w-[min(90vw,1080px)] md:h-auto max-w-none object-contain float-animation-alt pointer-events-none origin-bottom"
            style={{
              transform: 'rotateY(0deg) rotateX(5deg)',
            }}
          />
        </div>
      </div>
    </section>
  );
}
